import * as React from "react"
import { Hidden, Row, Visible } from "react-grid-system"
import styled from "styled-components"
import Card from "../../atoms/Card"
import NumberNotification from "../../atoms/NumberNotification"
import Subtitle from "../../atoms/typography/Subtitle"
import PrimaryButton from "../../atoms/buttons/PrimaryButton"
import { ClearAllIcon } from "../../atoms/icons/ClearAllIcon"
import { SettingsIcon } from "../../atoms/icons/SettingsIcon"
import UserNotificationBar from "../../molecules/UserNotificationBar"
import { useMyUserNotifications } from "../../../hooks/useMyUserNotifications"
import LoadingSpinner from "../../atoms/LoadingSpinner"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import { useClearAllUserNotifications } from "../../../hooks/useClearAllUserNotifications"
import SecondaryButton from "../../atoms/buttons/SecondaryButton"
import { CheckmarkIcon } from "../../atoms/icons/CheckboxIcons"
import { CrossIcon } from "../../atoms/icons/CrossIcon"
import { theme } from "../../theme"
import NotificationSettingsSection from "../../organisms/NotificationSettingsSection"
import { MediaQuery } from "../../../utils/design-helpers"
import { useUpdateAllUserNotificationsEnabled } from "../../../hooks/useUpdateAllUserNotificationsEnabled"
import { useUpdateUserNotifyViaEmailEnabled } from "../../../hooks/useUpdateUserNotifyViaEmailEnables"
import H3 from "../../atoms/typography/H3"
import { ApolloError } from "@apollo/client"
import { captureExceptionWithContext } from "../../../utils/sentry"

const ContentContainer = styled.div`
  padding: ${(props) => props.theme.spacings.extreme};

  ${MediaQuery.XS} {
    padding: ${(props) => props.theme.spacings.small};
  }
`

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${(props) => props.theme.colors.greyDark};
`

interface UserNotificationApiType {
  createdAt: string
  id: number | string
  message: string
  read: boolean
  __typename: string
  apiPlanId: number | string
}

const NotificationsPage: React.FunctionComponent = () => {
  const { userNotifications, loading, refetch } = useMyUserNotifications()
  const [editSettings, setEditSettings] = React.useState(false)
  const [notificationsEnabled, setNotificationsEnabled] = React.useState(
    userNotifications?.notificationsEnabled ?? false,
  )
  const [notifyViaEmail, setNotifyViaEmail] = React.useState(userNotifications?.notifyViaEmail ?? false)

  React.useEffect(() => {
    refetch()
  }, [editSettings])

  React.useEffect(() => {
    setNotificationsEnabled(userNotifications?.notificationsEnabled)
    setNotifyViaEmail(userNotifications?.notifyViaEmail)
  }, [userNotifications])

  const unreadNotifications: Array<UserNotificationApiType> =
    !loading && userNotifications?.notificationMessages?.filter((not: UserNotificationApiType) => not.read === false)
  const pastNotifications: Array<UserNotificationApiType> =
    !loading && userNotifications?.notificationMessages?.filter((not: UserNotificationApiType) => not.read === true)

  const { updateAllUserNotificationsEnabled } = useUpdateAllUserNotificationsEnabled({
    onError: (apolloError: ApolloError) => {
      captureExceptionWithContext(apolloError, "error updating all user notifications enabled")
    },
  })

  const { updateUserNotifyViaEmailEnabled } = useUpdateUserNotifyViaEmailEnabled({
    onError: (apolloError: ApolloError) => {
      captureExceptionWithContext(apolloError, "error updating user notify via email")
    },
  })

  const { clearAllUserNotifications } = useClearAllUserNotifications({
    onError: (apolloError: ApolloError) => {
      captureExceptionWithContext(apolloError, "error clearing all user notifications")
    },
  })

  const onClearAllUserNotifications = async () => {
    try {
      await clearAllUserNotifications({})
    } catch (error) {
      captureExceptionWithContext(error, "error clearing all user notifications")
    }
  }

  const onClickSaveEditChanges = async () => {
    try {
      await updateAllUserNotificationsEnabled({
        variables: {
          enabled: notificationsEnabled,
        },
      })
      await updateUserNotifyViaEmailEnabled({
        variables: {
          enabled: notifyViaEmail,
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "error updating user notifications")
      return
    }

    setEditSettings(false)
  }

  return (
    <>
      <Visible xs>
        {editSettings ? (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <SecondaryButton
              title="Save"
              icon={<CheckmarkIcon color={theme.colors.white} />}
              onClick={onClickSaveEditChanges}
              disabled={loading}
            />
            <Spacer spacing={2} />
            <PrimaryButton title="Cancel" icon={<CrossIcon />} onClick={() => setEditSettings(false)} />
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <PrimaryButton title="Clear all" icon={<ClearAllIcon />} onClick={onClearAllUserNotifications} />
            <Spacer spacing={2} />
            <PrimaryButton title="Settings" icon={<SettingsIcon />} onClick={() => setEditSettings(true)} />
          </div>
        )}
        <HorizontalSpacer spacing={2} />
      </Visible>
      <Card>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <ContentContainer>
            <Row justify="between" align="center">
              {editSettings ? (
                <Subtitle>Notification settings</Subtitle>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "0 15px",
                  }}
                >
                  <Subtitle>Unread notifications</Subtitle>
                  <Spacer spacing={1} />
                  <NumberNotification number={unreadNotifications?.length} />
                </div>
              )}
              <Hidden xs>
                {editSettings ? (
                  <div style={{ display: "flex" }}>
                    <SecondaryButton
                      title="Save changes"
                      icon={<CheckmarkIcon color={theme.colors.white} />}
                      onClick={onClickSaveEditChanges}
                    />
                    <Spacer spacing={2} />
                    <PrimaryButton
                      title="Cancel without saving"
                      icon={<CrossIcon />}
                      onClick={() => setEditSettings(false)}
                    />
                  </div>
                ) : (
                  <div style={{ display: "flex" }}>
                    <PrimaryButton title="Clear all" icon={<ClearAllIcon />} onClick={onClearAllUserNotifications} />
                    <Spacer spacing={2} />
                    <PrimaryButton title="Settings" icon={<SettingsIcon />} onClick={() => setEditSettings(true)} />
                  </div>
                )}
              </Hidden>
            </Row>
            <HorizontalSpacer spacing={2} />
            {editSettings ? (
              <NotificationSettingsSection
                notificationsEnabled={notificationsEnabled}
                notifyViaEmail={notifyViaEmail}
                onChangeNotificationsEnabled={setNotificationsEnabled}
                onChangeNotifyViaEmail={setNotifyViaEmail}
                userEmail={userNotifications?.email}
              />
            ) : (
              <div>
                {unreadNotifications.map((not: UserNotificationApiType, index: number) => (
                  <div key={`${not.id} ${index}`}>
                    <UserNotificationBar
                      read={not.read}
                      message={not.message}
                      id={not.id}
                      isSinglePlanNotification={not.__typename === "SingleDataPlanNotificationMsgType"}
                      apiPlanId={not.apiPlanId}
                    />
                    <HorizontalSpacer spacing={1} />
                  </div>
                ))}

                <Divider />
                <HorizontalSpacer spacing={2} />
                <H3 textAlign="left">Past notifications</H3>
                <HorizontalSpacer spacing={1} />

                {pastNotifications.map((not: UserNotificationApiType, index: number) => (
                  <div key={`${not.id} ${index}`}>
                    <UserNotificationBar
                      read={not.read}
                      message={not.message}
                      id={not.id}
                      isSinglePlanNotification={not.__typename === "SingleDataPlanNotificationMsgType"}
                      apiPlanId={not.apiPlanId}
                    />
                    <HorizontalSpacer spacing={1} />
                  </div>
                ))}
              </div>
            )}
          </ContentContainer>
        )}
      </Card>
    </>
  )
}

export default NotificationsPage
